// Site-information button between reload and the address bar (like Chrome's lock/tune icon):
// per-site permissions and clearing the site's cookies.
import { useEffect, useState } from 'react';
import { Lock, SlidersHorizontal, Trash2, TriangleAlert } from 'lucide-react';
import type { Api } from '../core/api';
import { t, tKind } from '../core/i18n';
import type { PermissionBroker } from '../core/permissions';
import { hostOf, isSecure, originOf } from '../core/url';

const KINDS = ['camera', 'microphone', 'geolocation', 'notifications'] as const;
type Choice = 'ask' | 'allow' | 'block';

export function SiteInfo(p: {
  api: Api;
  broker: PermissionBroker;
  url: string;
  canClearCookies: boolean;
  onSettings: () => void;
  onToast: (s: string) => void;
  up?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [choices, setChoices] = useState<Record<string, Choice>>({});
  const origin = originOf(p.url);
  const usable = /^https?:\/\//.test(p.url);

  useEffect(() => {
    if (!open || !usable) return;
    p.api
      .permissions()
      .then((list) => {
        const c: Record<string, Choice> = {};
        for (const x of list) if (x.origin === origin) c[x.kind] = x.allow ? 'allow' : 'block';
        setChoices(c);
      })
      .catch(() => {});
  }, [open, origin]);

  if (!usable) return null;

  const change = async (kind: string, v: Choice) => {
    setChoices((c) => ({ ...c, [kind]: v }));
    if (v === 'ask') await p.broker.forget(origin, kind);
    else {
      await p.api.setPermission(origin, kind, v === 'allow').catch(() => {});
      await p.broker.load();
    }
  };

  const secure = isSecure(p.url);
  return (
    <div className="menu-wrap">
      <button className="icon-btn" aria-label={t('siteInfo')} title={t('siteInfo')} onClick={() => setOpen((o) => !o)}>
        {secure ? <Lock size={14} /> : <TriangleAlert size={14} />}
      </button>
      {open && (
        <>
          <div className="menu-backdrop" onClick={() => setOpen(false)} />
          <div className={'menu site-info' + (p.up ? ' up' : '')} role="dialog" aria-label={t('siteInfo')} style={{ left: 0, right: 'auto' }}>
            <div className="si-head">
              <div className="si-host">{hostOf(origin)}</div>
              <div className="muted si-sub">{secure ? t('secure') : t('notSecure')}</div>
            </div>
            <hr />
            {KINDS.map((k) => (
              <div className="si-row" key={k}>
                <span className="grow">{tKind(k)}</span>
                <select className="input select" value={choices[k] ?? 'ask'} onChange={(e) => change(k, e.target.value as Choice)}>
                  <option value="ask">{t('permAsk')}</option>
                  <option value="allow">{t('permAllow')}</option>
                  <option value="block">{t('permBlock')}</option>
                </select>
              </div>
            ))}
            <hr />
            {p.canClearCookies && (
              <button
                role="menuitem"
                onClick={async () => {
                  setOpen(false);
                  await p.api.deleteSiteData({ host: new URL(origin).hostname }).then(
                    () => p.onToast(t('cookiesCleared')),
                    () => p.onToast(t('errorGeneric')),
                  );
                }}
              >
                <Trash2 size={15} /> {t('clearSiteCookies')}
              </button>
            )}
            <button
              role="menuitem"
              onClick={() => {
                setOpen(false);
                p.onSettings();
              }}
            >
              <SlidersHorizontal size={15} /> {t('allSiteSettings')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
